<template>
  <NuxtLayout name="default">
    <section class="near-me">
      <translate tag="h1"> Lodges near me </translate>
      <translate tag="h2" class="subhead">
        Use our lodges near me tool to search for sites by proximity to where
        you are now.
      </translate>

      <span class="page-content">
        <base-submit-button
          class="primary button near-me-btn"
          :disabled="geolocationError"
          :submitting="isGeolocationLoading"
          @click="geolocationMachine.send('LOCATE')"
        >
          <template #submittingText>
            <translate>Fetching your location...</translate>
          </template>
          <img
            src="@/assets/images/map-pin.svg"
            width="41"
            height="41"
            alt=""
          />
          <translate>Show lodges near me</translate>
        </base-submit-button>

        <geolocation-error
          v-if="isGeolocationFailed"
          :geolocation-error="geolocationError"
        />
        <div class="near-me-img-wrapper">
          <img
            src="@/assets/near-me/lodges-near-me.jpg"
            srcset="
              ~/assets/near-me/lodges-near-me.jpg        720w,
              ~/assets/near-me/lodges-near-me-large.jpg 2000w
            "
            sizes="100vw"
            :alt="$t('Lodges near me')"
            width="360"
            height="240"
            fetchpriority="high"
            decoding="sync"
          />
        </div>

        <translate tag="p"
          >Experience the great outdoors, without the worry about the weather,
          from the comfort of a lodge. These wooden structures come in all
          different shapes and sizes, whether it's a cabin with a private hot
          tub, camping pod or shepherd hut there is a lodge to suit everyone's
          taste and budgets. And the best part is you do not need to travel far
          to experience this type of holiday, as often a change of scenery for a
          few days is all you need to help unwind and relax.
        </translate>
        <translate tag="p" :tt="{ within: defaultDistance }"
          >To find lodges or log cabins that are within {within, distance, long}
          of you (as the crow flies), all you need to do is click the button
          below, granting us permission to use your location.
        </translate>
        <translate tag="p"
          >You can then filter the campsite list by over 80 criteria to find a
          lodge that meets your every need, whether that is a private hot tub,
          has disabled facilities, is dog friendly or even near the beach
          Pitchup will have the perfect option only a short drive from your
          house.
        </translate>
      </span>

      <section class="near-me-page-links">
        <translate tag="h2"> Other popular searches </translate>

        <nuxt-link :to="$puLocalePath({ name: 'campsites-near-me' })">
          <translate>Camping near me</translate>
        </nuxt-link>

        <nuxt-link :to="$puLocalePath({ name: 'glamping-near-me' })">
          <translate>Glamping near me</translate>
        </nuxt-link>

        <nuxt-link :to="$puLocalePath({ name: 'caravans-near-me' })">
          <translate>Caravan parks near me</translate>
        </nuxt-link>

        <nuxt-link :to="$puLocalePath({ name: 'tourers-near-me' })">
          <translate>Touring caravan sites near me</translate>
        </nuxt-link>
      </section>

      <nav class="breadcrumbs">
        <span class="breadcrumbs-generator">
          <a
            :href="`https://${$config.public.envUrl}/${
              $i18n.locale !== 'en-gb' ? $i18n.locale + '/' : ''
            }`"
          >
            <translate>Home</translate> </a
          ><span class="crumb">&rsaquo;</span>
          <a
            :href="`https://${$config.public.envUrl}/${
              $i18n.locale !== 'en-gb' ? $i18n.locale + '/' : ''
            }lodges/`"
          >
            <translate>Lodges</translate> </a
          ><span class="crumb">&rsaquo;</span>
          <translate>Lodges near me</translate>
        </span>
      </nav>
      <nuxt-to-meta :nuxt2meta="head" />
    </section>
  </NuxtLayout>
</template>

<script setup lang="ts">
import generateNearMeUrl from '~/utility/generateNearMeUrl'
import { Distance } from '~/utility/geo/Distance'
import {
  GeolocationContext,
  GeolocationState,
  getGeolocationMachine,
} from '~/utility/geolocation/machine'
import SCHEMA_ORGANIZATION from '../../json/schemaOrganization.json'
import { computed, ref, watch, onMounted } from 'vue'

definePageMeta({
  layout: false,
})

const imageGlob = import.meta.glob<{ default: string }>(
  '@/assets/near-me/*.jpg',
  {
    query: '?url',
    eager: true,
  },
)

// Get Nuxt app context
const { $t, $config, $puLocalePath, $i18n } = useNuxtApp()
const route = useRoute()
const router = useRouter()

// Geolocation machine state
const geolocationMachine = ref<ReturnType<typeof getGeolocationMachine>>()
const geolocationContext = ref<GeolocationContext | null>(null)
const geolocationState = ref<GeolocationState | null>(null)

// Computed properties
const _breadcrumbs = computed(() => [{ label: $t('Lodges near me') }])

const defaultDistance = computed(() =>
  Distance.new({ mi: $config.public.defaultNearMeWithinRange }),
)

const isGeolocationFailed = computed(
  () => geolocationState.value?.value === 'error',
)

const isGeolocationLoading = computed(
  () => geolocationState.value?.value === 'locating',
)

const geolocationError = computed(() => geolocationContext.value?.errorStatus)

const head = computed(() => ({
  title: $t('Lodges and log cabins near me') + ' | Pitchup.com',
  meta: [
    {
      hid: 'description',
      name: 'description',
      property: 'og:description',
      content: $t(
        'Find and book a lodge or log cabin near you. With over 80 filters available, including lodges with hot tubs, in forests or that are pet friendly, you will find your perfect lodge in your local area on Pitchup.',
      ),
    },
  ],
  link: [
    {
      rel: 'preload',
      as: 'image',
      href: imageGlob['/assets/near-me/lodges-near-me.jpg'].default,
      imagesrcset: `${imageGlob['/assets/near-me/lodges-near-me.jpg'].default} 720w, ${imageGlob['/assets/near-me/lodges-near-me-large.jpg'].default} 2000w`,
      imagesizes: '100vw',
      fetchpriority: 'high',
    },
  ],
  script: [
    {
      type: 'application/ld+json',
      body: true,
      json: {
        '@context': 'https://schema.org',
        '@type': 'ImageObject',
        name: $t('Lodges near me'),
        contentUrl: `https://${$config.public.envUrl}${imageGlob['/assets/near-me/lodges-near-me-large.jpg'].default}`,
      },
    },
    {
      type: 'application/ld+json',
      body: true,
      json: SCHEMA_ORGANIZATION,
      hid: 'schema-organization',
    },
    breadcrumbList.value,
  ],
}))

const breadcrumbList = computed(() => {
  const json = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      {
        '@type': 'ListItem',
        position: 1,
        item: {
          '@id': `https://${$config.public.envUrl}/${
            $i18n.locale !== 'en-gb' ? $i18n.locale + '/' : ''
          }`,
          name: 'Pitchup.com',
        },
      },
      {
        '@type': 'ListItem',
        position: 2,
        item: {
          '@id': `https://${$config.public.envUrl}/${
            $i18n.locale !== 'en-gb' ? $i18n.locale + '/' : ''
          }lodges/`,
          name: $t('Lodges'),
        },
      },
      {
        '@type': 'ListItem',
        position: 3,
        item: {
          '@id': `https://${$config.public.envUrl}${route.path}`,
          name: $t('Lodges near me'),
        },
      },
    ],
  }
  return {
    type: 'application/ld+json',
    body: true,
    json,
  }
})

// Functions
const startGeolocationMachine = () => {
  geolocationMachine.value = getGeolocationMachine()
    .onTransition((state) => {
      geolocationState.value = state
      geolocationContext.value = state.context
    })
    .start()
}

const redirectToSearch = () => {
  if (!geolocationContext.value?.point) {
    return
  }
  const url = generateNearMeUrl(
    'lodges',
    route.query,
    geolocationContext.value.point,
    $i18n.locale,
  )

  void router.push({
    path: $puLocalePath(url.path),
    query: url.query as Record<string, any>,
  })
}

// Lifecycle
onMounted(async () => {
  // eslint-disable-next-line @typescript-eslint/await-thenable -- startGeolocationMachine may be async in some contexts
  await startGeolocationMachine()
})

// Watchers
watch(geolocationState, (newState) => {
  if (newState?.value === 'success') {
    redirectToSearch()
  }
})
</script>
<style>
@import url('@/assets/css/near-me.css');
</style>
